// services/paymentApi.ts
import axiosInstance from '../utils/axiosInstance';
import {
  PaymentInitResponse,
  DueAmountResponse,
  PaymentStatusResponse,
  PaymentHistoryResponse,
  SubscriptionResponse,
  SubscriptionHistoryResponse,
  SavedCardsResponse,
  SubscriptionInterval,
} from '../types/payment.types';

const BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('viart_token');
  return { Authorization: `Bearer ${token}` };
};

// ─── Due Amount ────────────────────────────────────────────────────────────────
export const getDueAmount = async (
  quantity: number = 0,
  type: string = 'activation',
  targetPackage?: string
): Promise<DueAmountResponse> => {
  const res = await axiosInstance.get(`${BASE}/api/payment/due-amount`, {
    params: { quantity, type, ...(targetPackage ? { targetPackage } : {}) },
    headers: getAuthHeaders(),
  });
  return res.data;
};

// ─── Initialize Payment ────────────────────────────────────────────────────────
export const initializePayment = async (data?: {
  amount?: number;
  currency?: string;
  lang?: string;
  cardHolderID?: string;
  metadata?: Record<string, any>;
}): Promise<PaymentInitResponse> => {
  const res = await axiosInstance.post(
    `${BASE}/api/payment/initialize`,
    data || {},
    { headers: getAuthHeaders() }
  );
  return res.data;
};

// ─── Payment Status ────────────────────────────────────────────────────────────
export const getPaymentStatus = async (
  paymentId: string
): Promise<PaymentStatusResponse> => {
  const res = await axiosInstance.get(
    `${BASE}/api/payment/${paymentId}/status`,
    { headers: getAuthHeaders() }
  );
  return res.data;
};

// ─── Payment History ───────────────────────────────────────────────────────────
export const getPaymentHistory = async (): Promise<PaymentHistoryResponse> => {
  const res = await axiosInstance.get(`${BASE}/api/payment/history`, {
    headers: getAuthHeaders(),
  });
  return res.data;
};

// ─── URL Safety Validator ──────────────────────────────────────────────────────
// Only follow payment redirects to the Ameria Bank VPOS domain over HTTPS.
export const isSafePaymentUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname.endsWith('.ameriabank.am');
  } catch {
    return false;
  }
};

// ─── Process Callback (poll after Ameria redirect) ────────────────────────────
export const processCallback = async (params: {
  orderID: string;
  paymentID: string;
  responseCode?: string;
  opaque?: string;
}): Promise<PaymentStatusResponse> => {
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined)
    ) as Record<string, string>
  ).toString();
  const res = await axiosInstance.get(`${BASE}/api/payment/callback?${qs}`, {
    headers: getAuthHeaders(),
  });
  return res.data;
};

// ─── Subscriptions ─────────────────────────────────────────────────────────────

export const initSubscription = async (data: {
  planType: string;
  interval?: SubscriptionInterval;
  lang?: string;
}): Promise<PaymentInitResponse> => {
  const res = await axiosInstance.post(
    `${BASE}/api/subscriptions/init`,
    data,
    { headers: getAuthHeaders() }
  );
  return res.data;
};

export const getMySubscription = async (): Promise<SubscriptionResponse> => {
  const res = await axiosInstance.get(`${BASE}/api/subscriptions/me`, {
    headers: getAuthHeaders(),
  });
  return res.data;
};

export const getSubscriptionHistory = async (): Promise<SubscriptionHistoryResponse> => {
  const res = await axiosInstance.get(`${BASE}/api/subscriptions/history`, {
    headers: getAuthHeaders(),
  });
  return res.data;
};

export const getSavedCards = async (): Promise<SavedCardsResponse> => {
  const res = await axiosInstance.get(`${BASE}/api/subscriptions/saved-cards`, {
    headers: getAuthHeaders(),
  });
  return res.data;
};
export const cancelSubscription = async (reason?: string): Promise<SubscriptionResponse> => {
  const res = await axiosInstance.post(
    `${BASE}/api/subscriptions/cancel`,
    { reason },
    { headers: getAuthHeaders() }
  );
  return res.data;
};

// Manually charge the saved card now to settle a past-due subscription.
export const renewSubscriptionNow = async (subscriptionId: string): Promise<SubscriptionResponse> => {
  const res = await axiosInstance.post(
    `${BASE}/api/subscriptions/${subscriptionId}/renew`,
    {},
    { headers: getAuthHeaders() }
  );
  return res.data;
};

export const changeSubscriptionPlan = async (planType: string): Promise<SubscriptionResponse> => {
  const res = await axiosInstance.post(
    `${BASE}/api/subscriptions/change-plan`,
    { planType },
    { headers: getAuthHeaders() }
  );
  return res.data;
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const CURRENCY_MAP: Record<string, { code: string; locale: string }> = {
  '051': { code: 'AMD', locale: 'hy-AM' },
  '840': { code: 'USD', locale: 'en-US' },
  '978': { code: 'EUR', locale: 'de-DE' },
  '643': { code: 'RUB', locale: 'ru-RU' },
};

export const formatCurrency = (amount: number, currency: string): string => {
  const info = CURRENCY_MAP[currency] || { code: 'AMD', locale: 'hy-AM' };
  const formatted = new Intl.NumberFormat(info.locale, {
    style: 'currency',
    currency: info.code === '֏' ? 'AMD' : info.code,
    minimumFractionDigits: currency === '051' ? 0 : 2,
  }).format(amount);

  return formatted.replace('AMD', '֏');
};

export const getCurrencyLabel = (currency: string): string => {
  const code = CURRENCY_MAP[currency]?.code || '֏';
  return code === 'AMD' ? '֏' : code;
};